import { Box, BoxProps, Image as ChakraImage } from '@chakra-ui/react'
import { useEffect, useState, useRef } from 'react'
import { getRarityColorMap, rarityType } from '../constants/skinPropertes'
import CornerLinesSmall from './Icons/CornerLinesSmall'

// Semi-transparent background SVG - minimal size
export const SKELETON_IMAGE_BASE64 =
  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQwIiBoZWlnaHQ9IjE4MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjQwIiBoZWlnaHQ9IjE4MCIgZmlsbD0idHJhbnNwYXJlbnQiICByeD0iOCIvPjwvc3ZnPg=='
/**
 * SkinCardImage Component with optimized image loading
 *
 * Loading flow:
 * 1. Display base64 gun image immediately
 * 2. Try to load imageSrc
 * 3. If imageSrc loads → show it
 * 4. If imageSrc fails → try to load fallback
 * 5. If fallback loads → show it
 * 6. If fallback fails → keep showing base64 image
 */
const SkinCardImage = ({
  children,
  isLoaded = true,
  imageSrc,
  fallback,
  rarity,
  treasureStyle,
  ...rest
}: {
  isLoaded?: boolean
  imageSrc: string
  fallback?: string
  rarity: rarityType
  treasureStyle?: React.CSSProperties
} & BoxProps) => {
  // Image states
  const [currentSrc, setCurrentSrc] = useState<string>(SKELETON_IMAGE_BASE64)
  const [loadingFallback, setLoadingFallback] = useState<boolean>(false)
  const imageRef = useRef<HTMLImageElement | null>(null)

  // Primary image loading logic
  useEffect(() => {
    if (!imageSrc || !isLoaded) return

    // Start with base64 image always
    setCurrentSrc(SKELETON_IMAGE_BASE64)
    setLoadingFallback(false)

    const img = new Image()

    img.onload = () => {
      // When main image loads successfully, display it
      setCurrentSrc(imageSrc)
    }

    img.onerror = () => {
      console.error(`Failed to load image: ${imageSrc}`)

      // If main image fails, try fallback if available
      if (fallback && !loadingFallback) {
        setLoadingFallback(true)
        const fallbackImg = new Image()

        fallbackImg.onload = () => {
          // When fallback image loads successfully, display it
          setCurrentSrc(fallback)
        }

        fallbackImg.onerror = () => {
          console.error(`Failed to load fallback image: ${fallback}`)
        }

        fallbackImg.src = fallback
      }
    }

    // Start loading the main image
    img.src = imageSrc

    // Cleanup
    return () => {
      img.onload = null
      img.onerror = null
    }
  }, [imageSrc, fallback, isLoaded])

  return (
    <Box
      maxW={isLoaded ? ['65%'] : ['35%', '50%']}
      margin={'0 auto'}
      position={'relative'}
      top={'8%'}
      {...rest}
    >
      {isLoaded && (
        <Box
          width={'100%'}
          position={'absolute'}
          top={'50%'}
          left={'50%'}
          transform={'translate(-50%, -50%) scale(1.1)'}
          zIndex={-1}
        >
          <CornerLinesSmall
            color={getRarityColorMap(rarity as unknown as rarityType).border}
            width={'100%'}
            strokeWidth="1"
          />
        </Box>
      )}

      <ChakraImage
        ref={imageRef}
        src={currentSrc}
        // Just a safeguard, our manual handling should prevent this being needed
        fallbackSrc={SKELETON_IMAGE_BASE64}
        alt="skin"
        className="pbox-card_treasure"
        style={{
          ...treasureStyle,
          transition: 'opacity 0.3s ease-in-out',
        }}
        loading="eager"
        fetchPriority="high"
      />
    </Box>
  )
}

export default SkinCardImage
